import { useState } from 'react';
import { X, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmRelationshipModalProps {
  onClose: () => void;
  onConfirm: (confidence: 'certain' | 'estimated' | 'unknown') => void;
  memberData: {
    name: string;
    species: string;
    gender?: string;
    breed?: string;
    approximateAge?: string;
    color?: string;
    breederName?: string;
    notes?: string;
  };
  relationship: string;
  relationshipDisplayLabel?: string;
}

export function ConfirmRelationshipModal({
  onClose,
  onConfirm,
  memberData,
  relationship,
  relationshipDisplayLabel,
}: ConfirmRelationshipModalProps) {
  const { t } = useTranslation();
  const [confidence, setConfidence] = useState<
    'certain' | 'estimated' | 'unknown'
  >('certain');
  const displayRelationship = relationshipDisplayLabel ?? relationship;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(confidence);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-xl w-full">
        <div className="bg-gradient-to-r from-green-600 to-teal-600 dark:from-green-700 dark:to-teal-700 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('family.modals.confirmRelationshipTitle')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('family.modals.finalStep')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/20 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('family.modals.addingToFamilyTree')}
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/30 dark:to-teal-900/30 rounded-full flex items-center justify-center text-2xl">
                {memberData.species === 'Dog'
                  ? '🐕'
                  : memberData.species === 'Cat'
                    ? '🐈'
                    : '🐾'}
              </div>
              <div>
                <div
                  className="mb-1 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {memberData.name}
                </div>
                <div
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {memberData.breed || memberData.species}
                </div>
              </div>
            </div>
            <div className="text-sm bg-white dark:bg-[#16151D] rounded p-2 border border-gray-200 dark:border-white/20">
              <span
                className="text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('family.modals.relationshipLabel')}
              </span>
              <span
                className="ml-2 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {displayRelationship}
              </span>
            </div>
          </div>

          <div className="mb-6">
            <label
              className="block text-sm text-gray-700 dark:text-white/90 mb-3"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              {t('family.modals.howConfident')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <p
              className="text-xs text-gray-500 dark:text-white/50 mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('family.modals.confidenceHelp')}
            </p>

            <div className="space-y-3">
              <label
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  confidence === 'certain'
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-white/20 hover:border-green-300 dark:hover:border-green-600'
                }`}
              >
                <input
                  type="radio"
                  name="confidence"
                  value="certain"
                  checked={confidence === 'certain'}
                  onChange={(e) => setConfidence(e.target.value as 'certain')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <span
                      className="text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('family.modals.certain')}
                    </span>
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.certainDesc')}
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  confidence === 'estimated'
                    ? 'border-yellow-500 dark:border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                    : 'border-gray-200 dark:border-white/20 hover:border-yellow-300 dark:hover:border-yellow-600'
                }`}
              >
                <input
                  type="radio"
                  name="confidence"
                  value="estimated"
                  checked={confidence === 'estimated'}
                  onChange={(e) => setConfidence(e.target.value as 'estimated')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    <span
                      className="text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('family.modals.estimated')}
                    </span>
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.estimatedDesc')}
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  confidence === 'unknown'
                    ? 'border-gray-500 dark:border-gray-400 bg-gray-50 dark:bg-gray-900/20'
                    : 'border-gray-200 dark:border-white/20 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="confidence"
                  value="unknown"
                  checked={confidence === 'unknown'}
                  onChange={(e) => setConfidence(e.target.value as 'unknown')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span
                      className="text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('family.modals.unknown')}
                    </span>
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.unknownDesc')}
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-blue-900 dark:text-blue-100"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div className="mb-1">
                💡 {t('family.modals.confidenceLevels')}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                {t('family.modals.confidenceLevelsHelp')}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/20 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-gray-700 dark:text-white/90 mb-2"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              {t('family.modals.badgePreview')}
            </div>
            <div className="inline-flex items-center gap-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded">
              🟡 {t('family.modals.ownerAdded')}
              {confidence === 'certain' && ` • ${t('family.modals.certain')}`}
              {confidence === 'estimated' &&
                ` • ${t('family.modals.estimated')}`}
              {confidence === 'unknown' && ` • ${t('family.modals.unknown')}`}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 dark:from-green-700 dark:to-teal-700 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <CheckCircle className="w-5 h-5" />
              {t('family.modals.confirmAddToTree')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
